import { skipToken } from '@reduxjs/toolkit/query';
import { BenefitTypeOptions } from '@strata/constants';
import { Button } from '@strata/react-buttons';
import { CollapsibleSection } from '@strata/react-collapsible-section';
import { NumberInputFormik, TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { getIn, useFormikContext } from 'formik';
import PropTypes from 'prop-types';

import { Sections } from 'services/priorAuthorizationHelpers';
import {
    useGetConfiguredProductsForPayerQuery,
    useGetProductByOidQuery,
} from 'store/api/referenceApi';

const getId = (fieldId) => `${Sections.PRODUCT}.${fieldId}`;

export const ProductSection = ({ setOpenSection }) => {
    const formik = useFormikContext();
    const { healthPlan } = getIn(formik.values, Sections.PATIENT);
    const { data: productsList, isFetching: isProductsListFetching } =
        useGetConfiguredProductsForPayerQuery(
            healthPlan ? { segmentId: healthPlan.secondaryData } : skipToken,
        );

    const { oid: productOid } = getIn(formik.values, getId('productOid')) ?? {};
    const { data: productData, isLoading: productLoading } = useGetProductByOidQuery(productOid, {
        skip: !productOid,
    });
    const productServiceIdList = (productData?.codes ?? []).map((code) => ({
        name: code.code,
        ...code,
    }));

    const productErrors = getIn(formik.errors, Sections.PRODUCT);

    return (
        <>
            <SuggestSingleSelectFormik
                id={getId('benefit')}
                label="Benefit"
                helperText="Select which benefit to use for determining available products."
                placeholderText="Select Benefit"
                values={BenefitTypeOptions}
                onChange={async (newValue) => {
                    await formik.setFieldValue(getId('benefit'), newValue);
                    if (newValue !== getIn(formik.values, getId('benefit'))) {
                        await formik.setFieldValue(getId('productOid'), undefined);
                        await formik.setFieldValue(getId('productService'), undefined);
                    }
                }}
                required
            />
            <SuggestSingleSelectFormik
                id={getId('productOid')}
                label="Product"
                placeholderText="Select Product"
                disabled={!getIn(formik.values, getId('benefit'))}
                isLoading={isProductsListFetching}
                values={(productsList ?? []).filter(
                    (product) => product.benefit === getIn(formik.values, getId('benefit'))?.value,
                )}
                onChange={async (newValue) => {
                    await formik.setFieldValue(getId('productOid'), newValue);
                    if (newValue !== getIn(formik.values, getId('productOid'))) {
                        await formik.setFieldValue(getId('productService'), undefined);
                    }
                }}
                required
            />
            <SuggestSingleSelectFormik
                id={getId('productService')}
                label="Product Service ID"
                placeholderText="Select Product Service ID"
                disabled={!getIn(formik.values, getId('productOid'))}
                isLoading={productLoading}
                values={productServiceIdList}
                required
            />
            <NumberInputFormik
                id={getId('productDays')}
                label="Product Days Supply"
                step={1}
                maxDecimalPlaces={0}
                required
            />
            <NumberInputFormik id={getId('productQty')} label="Product Quantity" required />
            <div className="padding__bottom--medium">
                <CollapsibleSection
                    title="Additional Product Detail"
                    renderExpanded={false}
                    content={
                        <>
                            <TextInputFormik id={getId('strength')} label="Strength" />
                            <NumberInputFormik
                                id={getId('rxRefillCount')}
                                label="Prescription Refill Count"
                                step={1}
                                maxDecimalPlaces={0}
                            />
                            <TextInputFormik id={getId('rxSig')} label="Rx Sig" />
                            <NumberInputFormik id={getId('offerRate')} label="Offer Rate" />
                        </>
                    }
                />
            </div>
            <div style={{ display: 'flex', justifyContent: 'right' }}>
                <Button
                    intent={Button.Intent.PRIMARY}
                    disabled={!!productErrors}
                    label="Continue"
                    onClick={() => setOpenSection((state) => state + 1)}
                />
            </div>
        </>
    );
};

ProductSection.propTypes = {
    setOpenSection: PropTypes.func.isRequired,
};
